import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../DataTable";
import Alert from "../Alert";
import { listDeviceMaps, createDeviceMap, deleteDeviceMap } from "../../api/attendanceApi";
import "../../pages/Users/UsersPage.css";

/**
 * Shared list+create+delete UI for both student and staff biometric device
 * maps — same shape on both sides (personId + userId), differing only in
 * which API path and person-picker label to use. digicampusbd had two
 * separate near-identical pages/services (StudentDeviceMappingService,
 * StaffDeviceMapService) for this; consolidated into one component here.
 */
export default function DeviceMapManager({ subjectType, title, personLabel }) {
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [personId, setPersonId] = useState("");
  const [userId, setUserId] = useState("");

  const load = () => {
    setLoading(true);
    listDeviceMaps(subjectType)
      .then(setMaps)
      .catch(() => setError("Failed to load device maps."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [subjectType]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createDeviceMap(subjectType, { personId: Number(personId), userId });
      setSuccess("Device map created.");
      setPersonId("");
      setUserId("");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to create device map.");
    }
  };

  const handleDelete = async (map) => {
    if (!window.confirm(`Remove device mapping for "${map.personName}"?`)) {
      return;
    }
    setError("");
    try {
      await deleteDeviceMap(subjectType, map.id);
      setSuccess("Device map deleted.");
      setMaps((prev) => prev.filter((m) => m.id !== map.id));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete device map.");
    }
  };

  const columns = [
    { key: "customPersonId", label: "ID" },
    { key: "personName", label: "Name" },
    { key: "userId", label: "Device User ID" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>{title}</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <form onSubmit={handleCreate} className="user-form" style={{ marginBottom: 24 }}>
        <label>{personLabel} internal ID *</label>
        <input value={personId} onChange={(e) => setPersonId(e.target.value)} required placeholder="e.g. 175788" />
        <label>Device User ID *</label>
        <input value={userId} onChange={(e) => setUserId(e.target.value)} required placeholder="ID enrolled on the biometric device" />
        <div className="form-actions">
          <button type="submit" className="btn-primary">
            + Add Mapping
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={maps}
          actions={(map) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(map)}>
              Delete
            </button>
          )}
        />
      )}
    </MainLayout>
  );
}
